/**
 * MissionAssist global navigation definition.
 *
 * P4-PHASE1-NAV-REORG: This module is the SINGLE definition of every
 * destination P4 controls. Primary Rail, App Launcher and the mobile
 * navigation Sheet are all rendered from this one list — a destination is
 * declared exactly once, with a `placement` field, and never hard-coded
 * separately per surface.
 *
 * CRITICAL — RBAC scope boundary (P4 Phase 1):
 * This module performs NO authorization computation of its own. It consumes
 * the SAME `effectiveModules` array the route guards consume (produced by
 * `usePermissions()` from `GET /auth/me/access-context`) and applies the
 * SAME membership test that has always been applied here:
 *
 *     effectiveModules.includes(destination.requiredModule)
 *
 * Because `effective_modules` is built backend-side as
 * `[m for m, level in modules.items() if level != "off"]`, any access level
 * above `off` continues to make a destination eligible for navigation
 * visibility — unchanged from the previous implementation. Access-level
 * granularity (read/write/full) remains the destination's own concern and is
 * NOT consulted here.
 *
 * Agent Workspace is deliberately NOT a governed RBAC module. It keeps its
 * pre-existing `isAgentWorkspaceEnabled({ accountId })` predicate, evaluated
 * with the same account identity the route itself uses, so nav visibility and
 * route access cannot drift apart.
 *
 * MA-ARCH-201 Phase 3 (retained): Backend API is the source of truth.
 * Does NOT use user.role for any visibility logic.
 */

import {
  BookOpen,
  Target,
  Activity,
  Users,
  Contact,
  CalendarDays,
  Briefcase,
  Route,
  Filter,
  Layers,
  Megaphone,
  Radio,
  FlaskConical,
  BadgeDollarSign,
  Shield,
  Settings,
  Mail,
  Headphones,
} from 'lucide-react';
import {
  AGENT_WORKSPACE_ROUTE,
  isAgentWorkspaceEnabled,
} from '../features/agent-workspace/agentWorkspaceGate';

/** Placement values. A destination belongs to exactly one surface. */
export const NAV_PLACEMENT = Object.freeze({
  PRIMARY: 'primary',
  LAUNCHER: 'launcher',
});

/** Visibility strategies. `module` = standard RBAC module; `gate` = special predicate. */
export const NAV_VISIBILITY = Object.freeze({
  MODULE: 'module',
  GATE: 'gate',
});

/**
 * THE navigation definition.
 *
 * Array order is render order *within* each placement — there is no separate
 * sort field, and no separate per-surface list. Moving a destination between
 * the Primary Rail and the App Launcher is a one-word `placement` edit.
 *
 * Fields:
 *   id              stable key (also the RBAC module id for module-gated entries)
 *   label           user-facing application name
 *   icon            lucide component (see ICON NOTE below)
 *   path            destination route (unchanged by placement — P4 §15)
 *   placement       NAV_PLACEMENT.PRIMARY | NAV_PLACEMENT.LAUNCHER
 *   visibility      NAV_VISIBILITY.MODULE | NAV_VISIBILITY.GATE
 *   requiredModule  effective-module id (MODULE visibility only)
 *   isActive        (currentPath) => boolean — preserved verbatim from the
 *                   pre-P4 implementation for every pre-existing destination
 *   testid          existing data-testid, preserved so existing selectors hold
 *
 * ICON NOTE (P4 §11): icons are otherwise unchanged from the pre-P4 rail. The
 * single exception is Advertising, which previously shared `Target` with War
 * Room. With both now reachable at a glance (War Room on the rail, Advertising
 * in the launcher grid) an identical glyph was genuinely ambiguous, so
 * Advertising moves to `Radio` (broadcast/paid-reach) — distinct from both
 * `Target` (War Room) and `Megaphone` (Campaigns). No other icon changed.
 */
export const NAV_DESTINATIONS = Object.freeze([
  // ── PRIMARY RAIL — frequent operational applications ────────────────────
  {
    id: 'war_room',
    label: 'War Room',
    icon: Target,
    path: '/app/warroom',
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'war_room',
    isActive: (currentPath) => currentPath.includes('/warroom'),
    testid: 'side-nav-warroom',
  },
  {
    // Not an RBAC module by design. ENG-001 dev availability + BB6 production
    // account allowlist, evaluated with the caller-supplied accountId — the
    // same value AgentWorkspaceRoute.jsx passes — so rail visibility and route
    // access can never disagree.
    id: 'agent_workspace',
    label: 'Agent Workspace',
    icon: Headphones,
    path: AGENT_WORKSPACE_ROUTE,
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.GATE,
    isActive: (currentPath) => currentPath === AGENT_WORKSPACE_ROUTE,
    testid: 'side-nav-agent-workspace',
  },
  {
    id: 'leads',
    label: 'Leads',
    icon: Users,
    path: '/app/leads',
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'leads',
    isActive: (currentPath) =>
      currentPath.includes('/leads') && !currentPath.includes('/appointments'),
    testid: 'side-nav-leads',
  },
  {
    id: 'appointments',
    label: 'Appointments',
    icon: Contact,
    path: '/app/appointments',
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'appointments',
    isActive: (currentPath) =>
      currentPath.includes('/appointments') && !currentPath.includes('/calendar'),
    testid: 'side-nav-appointments',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: CalendarDays,
    path: '/app/calendar',
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'calendar',
    isActive: (currentPath) => currentPath.includes('/calendar'),
    testid: 'side-nav-calendar',
  },
  {
    id: 'clients',
    label: 'Clients',
    icon: Briefcase,
    path: '/app/clients',
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'clients',
    isActive: (currentPath) => currentPath.includes('/clients'),
    testid: 'side-nav-clients',
  },
  {
    id: 'emails',
    label: 'Emails',
    icon: Mail,
    path: '/app/emails',
    placement: NAV_PLACEMENT.PRIMARY,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'emails',
    isActive: (currentPath) => currentPath.includes('/emails'),
    testid: 'side-nav-emails',
  },

  // ── APP LAUNCHER — broader applications, builders, configuration ────────
  {
    id: 'playbooks',
    label: 'Playbooks',
    icon: BookOpen,
    path: '/app/playbooks',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'playbooks',
    isActive: (currentPath) =>
      currentPath === '/app/playbooks' || currentPath.startsWith('/app/playbooks/'),
    testid: 'side-nav-playbooks',
  },
  {
    id: 'runs',
    label: 'Runs',
    icon: Activity,
    path: '/app/runs',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'runs',
    isActive: (currentPath) => currentPath.includes('/runs'),
    testid: 'side-nav-runs',
  },
  {
    id: 'profit_paths',
    label: 'Profit Paths',
    icon: Route,
    path: '/app/profit-paths',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'profit_paths',
    isActive: (currentPath) => currentPath.includes('/profit-paths'),
    testid: 'side-nav-profit-paths',
  },
  {
    id: 'funnels',
    label: 'Funnels',
    icon: Filter,
    path: '/app/funnels',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'funnels',
    isActive: (currentPath) => currentPath.includes('/funnels'),
    testid: 'side-nav-funnels',
  },
  {
    id: 'landing_pages',
    label: 'Landing Pages',
    icon: Layers,
    path: '/app/landing-pages',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'landing_pages',
    isActive: (currentPath) => currentPath.includes('/landing-pages'),
    testid: 'side-nav-landing-pages',
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: Megaphone,
    path: '/app/campaigns',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'campaigns',
    isActive: (currentPath) =>
      currentPath.includes('/campaigns') && !currentPath.includes('/advertising'),
    testid: 'side-nav-campaigns',
  },
  {
    id: 'advertising',
    label: 'Advertising',
    icon: Radio, // see ICON NOTE above — was Target, which collided with War Room
    path: '/app/advertising',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'advertising',
    isActive: (currentPath) => currentPath.includes('/advertising'),
    testid: 'side-nav-advertising',
  },
  {
    id: 'experiments',
    label: 'Experiments',
    icon: FlaskConical,
    path: '/app/experiments',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'experiments',
    isActive: (currentPath) => currentPath.includes('/experiments'),
    testid: 'side-nav-experiments',
  },
  {
    id: 'commission_profiles',
    label: 'Commission Profiles',
    icon: BadgeDollarSign,
    path: '/app/commission-setup',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'commission_profiles',
    isActive: (currentPath) => currentPath.includes('/commission-setup'),
    testid: 'side-nav-commission-profiles',
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    path: '/app/admin',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'admin',
    isActive: (currentPath) => currentPath.includes('/admin'),
    testid: 'side-nav-admin',
  },
  {
    // P4-PHASE1: Settings moves out of the profile dropdown and into the App
    // Launcher, where it is subject to the SAME effective-module rule as every
    // other standard destination. The previous profile-menu entry rendered
    // unconditionally, with no `settings` check at all; this is strictly more
    // consistent with the route guard (App.js already wraps /app/settings in
    // RequireModule moduleId="settings"). How `settings` is *calculated* is
    // unchanged.
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/app/settings',
    placement: NAV_PLACEMENT.LAUNCHER,
    visibility: NAV_VISIBILITY.MODULE,
    requiredModule: 'settings',
    isActive: (currentPath) => currentPath.includes('/settings'),
    testid: 'side-nav-settings',
  },
]);

/**
 * Decide whether a single destination is visible to this user.
 *
 * This is the ONLY visibility decision in the navigation layer, and it is
 * shared by every surface. It introduces no new authorization computation:
 * module-gated destinations use the caller-supplied `effectiveModules`
 * exactly as before, and Agent Workspace uses its pre-existing predicate.
 */
function isDestinationVisible(destination, effectiveModules, accountId) {
  if (destination.visibility === NAV_VISIBILITY.GATE) {
    // Agent Workspace only. Same predicate + same account identity the route uses.
    return isAgentWorkspaceEnabled({ accountId });
  }
  return effectiveModules.includes(destination.requiredModule);
}

/**
 * Resolve the navigation for the current user, partitioned by placement.
 *
 * Filtering happens ONCE, over the single definition; the eligible result is
 * then partitioned. A destination can therefore never appear on one surface
 * but be missing from the other for authorization reasons, and can never
 * appear on both.
 *
 * @param {string[]} effectiveModules - effective module ids from usePermissions()
 * @param {string}   currentPath      - location.pathname
 * @param {Function} buildNavPath     - tenant-aware path builder (SuperAdmin URLs)
 * @param {string}   accountId        - selected account id (Agent Workspace gate)
 * @returns {{ primary: Array, launcher: Array }} render-ready nav items
 */
export function getNavigation(effectiveModules, currentPath, buildNavPath, accountId) {
  const modules = Array.isArray(effectiveModules) ? effectiveModules : [];
  const path = currentPath || '';
  const toPath = typeof buildNavPath === 'function' ? buildNavPath : (p) => p;

  const primary = [];
  const launcher = [];

  for (const destination of NAV_DESTINATIONS) {
    if (!isDestinationVisible(destination, modules, accountId)) continue;

    const item = {
      id: destination.id,
      label: destination.label,
      to: toPath(destination.path),
      icon: destination.icon,
      active: destination.isActive(path),
      testid: destination.testid,
    };

    if (destination.placement === NAV_PLACEMENT.LAUNCHER) {
      launcher.push(item);
    } else {
      primary.push(item);
    }
  }

  return { primary, launcher };
}

/**
 * Back-compat shim.
 *
 * Pre-P4 callers expected a single flat array of rail items. Retained so any
 * remaining caller keeps working, and so the mobile Sheet can fall back to a
 * flat list if ever needed. Returns primary followed by launcher.
 */

/**
 * P4-WP-3: is `pathname` reachable with these effective modules?
 *
 * Derived from NAV_DESTINATIONS -- there is deliberately no second route map.
 * AccountSwitcher previously kept its own hand-maintained routeModuleMap, which
 * had drifted: it was missing `emails`, so switching to an Account without the
 * Emails module while sitting on /app/emails left the user on a page they no
 * longer had. Deriving it here means a destination can never be added to the
 * rail and forgotten by the switch check.
 *
 * A path matching no known destination is treated as reachable, preserving the
 * previous default for shell routes that carry no module of their own.
 *
 * @param {string}   pathname         current route
 * @param {string[]} effectiveModules effective module ids from usePermissions()
 * @param {string}   accountId        selected account id (Agent Workspace gate)
 * @returns {boolean}
 */
export function isPathReachable(pathname, effectiveModules, accountId) {
  const modules = Array.isArray(effectiveModules) ? effectiveModules : [];
  const matches = NAV_DESTINATIONS.filter((d) => pathname.startsWith(d.path));
  if (matches.length === 0) return true;

  // Longest path wins, so /app/landing-pages is not decided by /app/landing.
  const destination = matches.reduce((a, b) =>
    b.path.length > a.path.length ? b : a
  );
  return isDestinationVisible(destination, modules, accountId);
}

/**
 * P4-WP-3: first destination the caller can actually reach.
 *
 * Uses NAV_DESTINATIONS order -- the same order the rail renders -- rather than
 * a separate hardcoded priority list. Falls back to '/app' when nothing is
 * visible, matching the previous behaviour.
 */
export function getFirstReachablePath(effectiveModules, accountId) {
  const modules = Array.isArray(effectiveModules) ? effectiveModules : [];
  const destination = NAV_DESTINATIONS.find((d) =>
    isDestinationVisible(d, modules, accountId)
  );
  return destination ? destination.path : '/app';
}

export function getLegacyNavItemsWithPermissions(
  effectiveModules,
  currentPath,
  buildNavPath,
  accountId
) {
  const { primary, launcher } = getNavigation(
    effectiveModules,
    currentPath,
    buildNavPath,
    accountId
  );
  return [...primary, ...launcher];
}
